import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Notiflix from 'notiflix';
import { 
  getOrganizations, 
  getOrganizationOverview,
  getOrganizationTypes,
  createOrganization,
  deleteOrganization,
  updateOrganization,
  formatOrganizationFromApi,
  formatOrganizationForApi,
  getOrganizationTypeLabel
} from '../services/organizations';
import { 
  FiPlus, 
  FiSearch, 
  FiFolder, 
  FiTrendingUp, 
  FiCalendar,
  FiFileText,
  FiTarget,
  FiEdit2,
  FiTrash2,
  FiEye,
  FiMoreVertical
} from 'react-icons/fi';
import { Button, Modal, ModalContent, ModalHeader, ModalBody, ModalFooter, Input, Textarea, Select, SelectItem, Card, CardBody, Chip, Avatar, Dropdown, DropdownTrigger, DropdownMenu, DropdownItem } from '@nextui-org/react';

const Organizations = () => {
  const navigate = useNavigate();
  const [organizations, setOrganizations] = useState([]);
  const [filteredOrganizations, setFilteredOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [organizationTypes, setOrganizationTypes] = useState([]);
  const [editingOrg, setEditingOrg] = useState(null);
  
  // Yeni organizasyon formu
  const [newOrg, setNewOrg] = useState({
    title: '',
    description: '',
    slug: '',
    type: 1
  });

  // Düzenleme formu
  const [editOrg, setEditOrg] = useState({
    title: '',
    description: '',
    type: 1
  });

  // Organizasyonları yükle
  useEffect(() => {
    fetchOrganizations();
    fetchOrganizationTypes();
  }, []);

  // Filtreleme
  useEffect(() => {
    let filtered = [...organizations];

    if (searchTerm) {
      filtered = filtered.filter(org => 
        org.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        org.description.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    setFilteredOrganizations(filtered);
  }, [organizations, searchTerm]);

  const fetchOrganizations = async () => {
    try {
      setLoading(true);
      setError(null);
      const orgs = await getOrganizations();
      const formattedOrgs = orgs.map(formatOrganizationFromApi);
      setOrganizations(formattedOrgs);
    } catch (error) {
      console.error('Organizasyonlar yüklenirken hata:', error);
      setError('Organizasyonlar yüklenirken bir hata oluştu.');
    } finally {
      setLoading(false);
    }
  };

  const fetchOrganizationTypes = async () => {
    try {
      const types = await getOrganizationTypes();
      setOrganizationTypes(types);
    } catch (error) {
      console.error('Organizasyon tipleri yüklenirken hata:', error);
    }
  };

  const handleCreateOrganization = async () => {
    if (!newOrg.title.trim() || !newOrg.description.trim() || !newOrg.slug.trim()) {
      Notiflix.Notify.warning('Lütfen tüm alanları doldurun.');
      return;
    }

    try {
      Notiflix.Loading.circle('Organizasyon oluşturuluyor...');
      const formData = {
        organization_title: newOrg.title,
        organization_description: newOrg.description,
        organization_slug: newOrg.slug,
        organization_type: newOrg.type
      };
      await createOrganization(formData);
      await fetchOrganizations();
      setIsCreateModalOpen(false);
      setNewOrg({ title: '', description: '', slug: '', type: 1 });
      Notiflix.Notify.success('Organizasyon başarıyla oluşturuldu!');
    } catch (error) {
      console.error('Organizasyon oluşturulurken hata:', error);
      Notiflix.Notify.failure(error.response?.data?.message || 'Organizasyon oluşturulurken bir hata oluştu.');
    } finally {
      Notiflix.Loading.remove();
    }
  };

  const handleEditOrganization = (org) => {
    setEditingOrg(org);
    setEditOrg({
      title: org.title,
      description: org.description,
      type: org.type
    });
    setIsEditModalOpen(true);
  };

  const handleUpdateOrganization = async () => {
    if (!editOrg.title.trim() || !editOrg.description.trim()) {
      Notiflix.Notify.warning('Lütfen tüm alanları doldurun.');
      return;
    }

    try {
      Notiflix.Loading.circle('Organizasyon güncelleniyor...');
      const formData = {
        organization_title: editOrg.title,
        organization_description: editOrg.description,
        organization_type: editOrg.type
      };
      await updateOrganization(editingOrg.slug, formData);
      await fetchOrganizations();
      setIsEditModalOpen(false);
      setEditingOrg(null);
      Notiflix.Notify.success('Organizasyon başarıyla güncellendi!');
    } catch (error) {
      console.error('Organizasyon güncellenirken hata:', error);
      Notiflix.Notify.failure(error.response?.data?.message || 'Organizasyon güncellenirken bir hata oluştu.');
    } finally {
      Notiflix.Loading.remove();
    }
  };

  const handleDeleteOrganization = async (slug, title) => {
    Notiflix.Confirm.show(
      'Organizasyonu Sil',
      `"${title}" organizasyonunu silmek istediğinizden emin misiniz? Bu işlem geri alınamaz.`,
      'Evet, Sil',
      'İptal',
      async () => {
        try {
          Notiflix.Loading.circle('Organizasyon siliniyor...');
          await deleteOrganization(slug);
          await fetchOrganizations();
          Notiflix.Notify.success('Organizasyon başarıyla silindi!');
        } catch (error) {
          console.error('Organizasyon silinirken hata:', error);
          Notiflix.Notify.failure(error.response?.data?.message || 'Organizasyon silinirken bir hata oluştu.');
        } finally {
          Notiflix.Loading.remove();
        }
      },
      () => {}
    );
  };

  const handleViewOrganization = (slug) => {
    navigate(`/organizations/${slug}`);
  };

  if (loading && organizations.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-dark-200 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#da402f] dark:border-[#7375E1] mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Organizasyonlar yükleniyor...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-dark-200 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
              Organizasyonlar
            </h1>
            <p className="text-gray-600 dark:text-gray-400">
              Tüm organizasyonları yönetin ve takip edin
            </p>
          </div>
          <Button
            onClick={() => setIsCreateModalOpen(true)}
            className="bg-[#da402f] dark:bg-[#7375E1] text-white hover:bg-[#c73a2a] dark:hover:bg-[#6b5fd1] mt-4 sm:mt-0"
            startContent={<FiPlus size={16} />}
          >
            Yeni Organizasyon
          </Button>
        </div>

        {/* Arama */}
        <div className="mb-6">
          <div className="relative max-w-md">
            <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
            <input
              type="text"
              placeholder="Organizasyonlarda ara..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-dark-300 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#da402f] dark:focus:ring-[#7375E1] focus:border-transparent"
            />
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-6 p-4 bg-red-100 dark:bg-red-900/30 border border-red-300 dark:border-red-700 rounded-lg">
            <p className="text-red-800 dark:text-red-300">{error}</p>
          </div>
        )}

        {/* Organizations List */}
        <Card className="bg-white dark:bg-dark-300 border border-gray-200 dark:border-gray-700 shadow-none">
          <CardBody className="p-0">
            {filteredOrganizations.length > 0 ? (
              <div className="divide-y divide-gray-200 dark:divide-gray-700">
                {filteredOrganizations.map((org) => (
                  <div
                    key={org.id}
                    className="p-4 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors group cursor-pointer"
                    onClick={() => handleViewOrganization(org.slug)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-4 flex-1">
                        {/* Organization Info */}
                        <div className="flex-1">
                          <div className="flex items-center space-x-3 mb-2">
                            <h3 className="text-lg font-semibold text-gray-900 dark:text-white group-hover:text-red-600 dark:group-hover:text-red-400 transition-colors">
                              {org.title}
                            </h3>
                            <Chip 
                              size="sm" 
                              variant="flat"
                              className="bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300"
                            >
                              {getOrganizationTypeLabel(org.type)}
                            </Chip>
                          </div>
                          <p className="text-gray-600 dark:text-gray-400 text-sm mb-2 line-clamp-1">
                            {org.description}
                          </p>
                          <div className="flex items-center text-sm text-gray-500 dark:text-gray-400">
                            <span>
                              Oluşturulma: {new Date(org.createdAt).toLocaleDateString('tr-TR')}
                            </span>
                          </div>
                        </div>
                      </div>
                      
                      {/* Actions */}
                      <div className="flex items-center space-x-2" onClick={(e) => e.stopPropagation()}>
                        <Button
                          size="sm"
                          variant="light"
                          className="text-gray-600 dark:text-gray-400 hover:text-red-600 dark:hover:text-red-400"
                          startContent={<FiEye size={16} />}
                          onClick={(e) => { e.stopPropagation(); handleViewOrganization(org.slug); }}
                        >
                          Görüntüle
                        </Button>
                        <Dropdown>
                          <DropdownTrigger>
                            <Button isIconOnly variant="light" size="sm" className="opacity-0 group-hover:opacity-100 transition-opacity">
                              <FiMoreVertical size={16} />
                            </Button>
                          </DropdownTrigger>
                          <DropdownMenu>
                            <DropdownItem 
                              key="edit" 
                              startContent={<FiEdit2 size={16} />}
                              onClick={() => handleEditOrganization(org)}
                            >
                              Düzenle
                            </DropdownItem>
                            <DropdownItem 
                              key="delete" 
                              startContent={<FiTrash2 size={16} />}
                              className="text-red-600"
                              onClick={() => handleDeleteOrganization(org.slug, org.title)}
                            >
                              Sil
                            </DropdownItem>
                          </DropdownMenu>
                        </Dropdown>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="p-8 text-center">
                <div className="p-4 bg-gray-100 dark:bg-gray-800 rounded-full w-16 h-16 mx-auto mb-4 flex items-center justify-center">
                  <FiFolder className="text-gray-400" size={24} />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                  {organizations.length === 0 ? 'Henüz organizasyon yok' : 'Arama sonucu bulunamadı'}
                </h3>
                <p className="text-gray-600 dark:text-gray-400 mb-4">
                  {organizations.length === 0 ? 'İlk organizasyonunuzu oluşturun' : 'Farklı arama terimleri deneyin'}
                </p>
                <Button
                  onClick={() => setIsCreateModalOpen(true)}
                  className="bg-[#da402f] dark:bg-[#7375E1] text-white hover:bg-[#c73a2a] dark:hover:bg-[#6b5fd1]"
                  startContent={<FiPlus size={16} />}
                >
                  {organizations.length === 0 ? 'İlk Organizasyonu Oluştur' : 'Yeni Organizasyon Oluştur'}
                </Button>
              </div>
            )}
          </CardBody>
        </Card>


        {/* Create Organization Modal */}
        <Modal isOpen={isCreateModalOpen} onClose={() => setIsCreateModalOpen(false)} size="lg">
          <ModalContent>
            <ModalHeader>
              <h2 className="text-xl font-semibold">Yeni Organizasyon Oluştur</h2>
            </ModalHeader>
            <ModalBody>
              <div className="space-y-4">
                <Input
                  label="Organizasyon Adı"
                  placeholder="Organizasyon adını girin"
                  value={newOrg.title}
                  onChange={(e) => {
                    const title = e.target.value;
                    const slug = title
                      .toLowerCase()
                      .replace(/ğ/g, 'g')
                      .replace(/ü/g, 'u')
                      .replace(/ş/g, 's')
                      .replace(/ı/g, 'i')
                      .replace(/ö/g, 'o')
                      .replace(/ç/g, 'c')
                      .replace(/[^a-z0-9]+/g, '-')
                      .replace(/^-+|-+$/g, '');
                    setNewOrg({ ...newOrg, title, slug });
                  }}
                  isRequired
                />
                <Input
                  label="Slug"
                  placeholder="organizasyon-slug"
                  value={newOrg.slug}
                  onChange={(e) => setNewOrg({ ...newOrg, slug: e.target.value })}
                  isRequired
                  description="URL-friendly benzersiz tanımlayıcı (otomatik oluşturuldu)"
                />
                <Textarea
                  label="Açıklama"
                  placeholder="Organizasyon açıklamasını girin"
                  value={newOrg.description}
                  onChange={(e) => setNewOrg({ ...newOrg, description: e.target.value })}
                  isRequired
                  rows={4}
                />
                <Select
                  label="Organizasyon Tipi"
                  placeholder="Tip seçin"
                  selectedKeys={[newOrg.type.toString()]}
                  onChange={(e) => setNewOrg({ ...newOrg, type: parseInt(e.target.value) })}
                >
                  {organizationTypes.map((type) => (
                    <SelectItem key={type.id.toString()} value={type.id.toString()}>
                      {type.name}
                    </SelectItem>
                  ))}
                </Select>
              </div>
            </ModalBody>
            <ModalFooter>
              <Button 
                variant="light" 
                onPress={() => setIsCreateModalOpen(false)}
              >
                İptal
              </Button>
              <Button 
                className="bg-[#da402f] dark:bg-[#7375E1] text-white hover:bg-[#c73a2a] dark:hover:bg-[#6b5fd1]"
                onPress={handleCreateOrganization}
              >
                Oluştur
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>

        {/* Edit Organization Modal */}
        <Modal isOpen={isEditModalOpen} onClose={() => setIsEditModalOpen(false)} size="lg">
          <ModalContent>
            <ModalHeader>
              <h2 className="text-xl font-semibold">Organizasyonu Düzenle</h2>
            </ModalHeader>
            <ModalBody>
              <div className="space-y-4">
                <Input
                  label="Organizasyon Adı"
                  placeholder="Organizasyon adını girin"
                  value={editOrg.title}
                  onChange={(e) => setEditOrg({ ...editOrg, title: e.target.value })}
                  isRequired
                />
                <Textarea
                  label="Açıklama"
                  placeholder="Organizasyon açıklamasını girin"
                  value={editOrg.description}
                  onChange={(e) => setEditOrg({ ...editOrg, description: e.target.value })}
                  isRequired
                  rows={4}
                />
                <Select
                  label="Organizasyon Tipi"
                  placeholder="Tip seçin"
                  selectedKeys={[editOrg.type.toString()]}
                  onChange={(e) => setEditOrg({ ...editOrg, type: parseInt(e.target.value) })}
                >
                  {organizationTypes.map((type) => (
                    <SelectItem key={type.id.toString()} value={type.id.toString()}>
                      {type.name}
                    </SelectItem>
                  ))}
                </Select>
              </div>
            </ModalBody>
            <ModalFooter>
              <Button 
                variant="light" 
                onPress={() => setIsEditModalOpen(false)}
              >
                İptal
              </Button>
              <Button 
                className="bg-[#da402f] dark:bg-[#7375E1] text-white hover:bg-[#c73a2a] dark:hover:bg-[#6b5fd1]"
                onPress={handleUpdateOrganization}
              >
                Güncelle
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      </div>
    </div>
  );
};

export default Organizations;
